import type { MiningController, MiningSnapshot } from "../mining-engine";
import introUrl from "../assets/audio/ancient-artifact-awakening/intro.wav";
import loopUrl from "../assets/audio/ancient-artifact-awakening/mining-loop.wav";
import revealUrl from "../assets/audio/ancient-artifact-awakening/reveal.wav";

type Stem = "intro" | "loop" | "reveal";
type Voice = { source: AudioBufferSourceNode; gain: GainNode; stem: Stem; retiring: boolean; targetLevel: number };
type Intent = { revision: number; identity: string; context: string; readContext(): string;
  introPending: boolean; phase: MiningSnapshot["phase"] };
const urls: Record<Stem, string> = { intro: introUrl, loop: loopUrl, reveal: revealUrl };
const preferenceKey = "greenwood.mining-sound";

export function readSoundPreference(): boolean {
  try { return localStorage.getItem(preferenceKey) !== "off"; } catch { return true; }
}

/** One runtime-owned, optional observer. Never delays or controls hashing/proof UI. */
export class MiningAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private buffers: Record<Stem, AudioBuffer> | null = null;
  private loading: Promise<void> | null = null;
  private readonly abort = new AbortController();
  private readonly voices = new Set<Voice>();
  private readonly revealed = new Set<string>();
  private readonly introduced = new Set<string>();
  private intent: Intent | null = null;
  private playbackContext: { key: string; read(): string } | null = null;
  private revision = 0;
  private ready = false;
  private disposed = false;
  private enabled = readSoundPreference();
  private readonly listeners = new Set<() => void>();
  private readonly unsubscribe: () => void;

  constructor(private readonly mining: MiningController) {
    this.unsubscribe = mining.subscribe(() => {
      try { this.observe(); } catch { this.cancel(); }
    });
  }

  readonly getEnabled = () => this.enabled;
  readonly subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  /** Call synchronously from Start/Resume/Retry/Unmute, before any await. */
  unlock(): number {
    const revision = this.revision;
    if (this.disposed) return revision;
    try {
      if (!this.context) {
        this.context = new AudioContext();
        this.master = this.context.createGain();
        this.master.gain.value = .42;
        this.master.connect(this.context.destination);
      }
      const context = this.context;
      if (context.state !== "running") this.fadeAll();
      // resume() is invoked in the gesture even when preference is muted.
      void context.resume().then(() => {
        if (this.disposed || this.context !== context) return;
        this.ready = context.state === "running";
        this.background();
      }).catch(() => { if (this.context === context) this.ready = false; });
      this.load(context);
    } catch { /* Unsupported/blocked audio is optional. Mining still proceeds. */ }
    return revision;
  }

  /** Arm only after action identity validation; restored VERIFIED is never armed. */
  arm(mode: "start" | "resume" | "retry", readContext: () => string, gestureRevision = this.revision): number | null {
    if (this.disposed || gestureRevision !== this.revision) return null;
    const snapshot = this.mining.getSnapshot();
    if (!snapshot.challenge || snapshot.phase === "VERIFIED") return null;
    const identity = challengeIdentity(snapshot);
    const context = readContext();
    if (this.intent?.identity === identity && this.intent.context === context) return this.intent.revision;
    this.cancel();
    const revision = this.revision;
    this.intent = { revision, identity, context, readContext, phase: snapshot.phase,
      introPending: mode === "start" && snapshot.activeElapsedMs === 0 && !snapshot.candidate && !this.introduced.has(identity) };
    this.playbackContext = { key: context, read: readContext };
    return revision;
  }

  cancel(revision?: number): void {
    if (revision !== undefined && this.intent?.revision !== revision) return;
    this.revision++;
    this.intent = null;
    this.playbackContext = null;
    this.fadeAll();
  }

  readonly setEnabled = (enabled: boolean): void => {
    if (this.disposed) return;
    this.enabled = enabled;
    try { localStorage.setItem(preferenceKey, enabled ? "on" : "off"); } catch { /* In-memory preference remains usable. */ }
    if (enabled) { this.unlock(); this.background(); }
    else this.fadeAll();
    for (const listener of this.listeners) listener();
  };

  checkContext(): void {
    if (this.playbackContext && this.playbackContext.key !== this.playbackContext.read()) this.cancel();
  }

  private load(context: AudioContext): void {
    if (this.buffers || this.loading) return;
    this.loading = Promise.all((Object.keys(urls) as Stem[]).map(async stem => {
      const response = await fetch(urls[stem], { signal: this.abort.signal });
      if (!response.ok) throw new Error("Mining sound unavailable");
      const buffer = await context.decodeAudioData(await response.arrayBuffer());
      return [stem, buffer] as const;
    })).then(entries => {
      if (this.disposed || this.context !== context) return;
      this.buffers = Object.fromEntries(entries) as Record<Stem, AudioBuffer>;
      // Late loads may join a current background, never replay an onset/reveal.
      this.background();
    }).catch(() => { /* Fetch/decode errors never escape into mining actions. */ })
      .finally(() => { this.loading = null; });
  }

  private current(): Intent | null {
    const intent = this.intent;
    if (!intent) return null;
    if (this.disposed ||
      intent.revision !== this.revision || intent.context !== intent.readContext() ||
      intent.identity !== challengeIdentity(this.mining.getSnapshot())) {
      this.cancel();
      return null;
    }
    return intent;
  }

  private observe(): void {
    const intent = this.current();
    if (!intent) return;
    const snapshot = this.mining.getSnapshot();
    const previous = intent.phase;
    intent.phase = snapshot.phase;
    if (snapshot.phase === "RUNNING" && !snapshot.tabOwner) { this.cancel(); return; }
    // Resume/Retry arm in the current phase, before challenge/lease updates.
    // Only entering a stop phase revokes that intent; metadata updates do not.
    if (previous !== snapshot.phase &&
      ["PAUSED", "ERROR", "EXPIRED", "CANCELLED", "SUBMIT_RETRY", "IDLE"].includes(snapshot.phase)) {
      this.cancel();
      return;
    }
    if (snapshot.phase === "VERIFIED") {
      const proof = snapshot.proof;
      const key = `${intent.identity}:${proof?.proofId}`;
      const accepted = previous === "SUBMITTING" && proof?.proofStatus === "VERIFIED" &&
        proof.challengeId.toLowerCase() === snapshot.challenge?.challengeId.toLowerCase();
      const fresh = accepted && !this.revealed.has(key);
      if (accepted) this.revealed.add(key);
      this.cancel();
      // Consume even when muted, suspended or unloaded. Never queue late success.
      if (fresh && this.playable()) {
        this.playbackContext = { key: intent.context, read: intent.readContext };
        this.play("reveal", 1);
      }
      return;
    }
    if (snapshot.phase === "RUNNING" && snapshot.tabOwner) {
      if (intent.introPending) {
        intent.introPending = false;
        this.introduced.add(intent.identity);
        if (this.playable()) {
          const intro = this.play("intro", 1);
          if (intro) {
            // Schedule on the audio clock: no JS timer competing with hashing.
            const when = this.context!.currentTime + Math.max(0, this.buffers!.intro.duration - .25);
            this.play("loop", 1, when);
            return;
          }
        }
      }
    } else if (["CANDIDATE_LOCAL", "SUBMITTING"].includes(snapshot.phase)) {
      intent.introPending = false;
      this.fadeStem("intro");
    }
    this.background();
  }

  private background(): void {
    const intent = this.current();
    if (!intent || !this.playable()) return;
    const snapshot = this.mining.getSnapshot();
    const running = snapshot.phase === "RUNNING" && snapshot.tabOwner;
    const waiting = ["CANDIDATE_LOCAL", "SUBMITTING"].includes(snapshot.phase);
    if (!running && !waiting) return;
    let voice = [...this.voices].find(item => item.stem === "loop" && !item.retiring);
    if (running && voice && this.context!.currentTime < (this.loopStart ?? 0)) return;
    // A loop scheduled after intro must become audible immediately on candidate.
    if (waiting && [...this.voices].some(item => item.stem === "intro" && !item.retiring)) this.fadeStem("intro");
    if (waiting && voice && voice.source.context.currentTime < (this.loopStart ?? 0)) {
      this.fadeStem("loop"); voice = undefined;
    }
    const level = waiting ? .32 : 1;
    if (!voice) this.play("loop", level);
    else if (voice.targetLevel !== level) {
      // Telemetry must not keep cancelling the fade-in of a resumed loop.
      this.ramp(voice.gain.gain, level, .18);
      voice.targetLevel = level;
    }
  }

  private loopStart: number | null = null;

  private playable(): boolean {
    return !this.disposed && this.enabled && this.ready && this.context?.state === "running" &&
      this.buffers !== null;
  }

  private play(stem: Stem, level: number, when = this.context?.currentTime ?? 0): Voice | null {
    if (!this.playable()) return null;
    let voice: Voice | null = null;
    try {
      const source = this.context!.createBufferSource();
      const gain = this.context!.createGain();
      voice = { source, gain, stem, retiring: false, targetLevel: level };
      source.buffer = this.buffers![stem];
      source.loop = stem === "loop";
      source.connect(gain); gain.connect(this.master!);
      gain.gain.setValueAtTime(0, when);
      gain.gain.linearRampToValueAtTime(level, when + (stem === "loop" ? .25 : .015));
      const tracked = voice;
      source.onended = () => this.release(tracked);
      this.voices.add(voice);
      source.start(when);
      if (stem === "loop") this.loopStart = when;
      return voice;
    } catch {
      if (voice) this.release(voice);
      return null;
    }
  }

  private ramp(param: AudioParam, level: number, seconds: number): void {
    const now = this.context!.currentTime;
    if (typeof param.cancelAndHoldAtTime === "function") param.cancelAndHoldAtTime(now);
    else { param.cancelScheduledValues(now); param.setValueAtTime(param.value, now); }
    param.linearRampToValueAtTime(level, now + seconds);
  }

  private fadeStem(stem: Stem): void { this.fadeAll(stem); }
  private fadeAll(stem?: Stem): void {
    for (const voice of this.voices) {
      if ((voice.retiring && this.context?.state === "running") || (stem && voice.stem !== stem)) continue;
      voice.retiring = true;
      // A suspended audio clock cannot complete a fade. Disconnect now so an
      // old voice cannot become audible when a later gesture resumes the clock.
      if (this.context?.state !== "running") {
        try { voice.source.stop(); } catch { /* Already stopped. */ }
        this.release(voice);
        continue;
      }
      try { this.ramp(voice.gain.gain, 0, .08); voice.source.stop(this.context!.currentTime + .08); }
      catch { this.release(voice); }
    }
  }

  private release(voice: Voice): void {
    voice.source.onended = null;
    voice.source.disconnect(); voice.gain.disconnect();
    this.voices.delete(voice);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.cancel(); this.abort.abort(); this.unsubscribe();
    for (const voice of this.voices) {
      try { voice.source.stop(); } catch { /* Already ended. */ }
      this.release(voice);
    }
    this.listeners.clear(); this.buffers = null;
    const context = this.context;
    this.context = null; this.master?.disconnect(); this.master = null;
    if (context) { try { void context.close().catch(() => {}); } catch { /* Already closed. */ } }
  }
}

function challengeIdentity(snapshot: MiningSnapshot): string {
  const c = snapshot.challenge;
  return c ? JSON.stringify([c.instanceId, c.chainId, c.walletAddress, c.tokenAddress,
    c.challengeId, c.challengeCommitment]).toLowerCase() : "";
}
